/**
 * API Client for Podcast Manager
 *
 * Uses the shared API client from the parent app via Module Federation.
 * This ensures consistent authentication and request handling across all apps.
 */

// Import shared API client from parent app via Module Federation
import { api, getApiBaseUrl } from 'shell/Api';
import type { ApiResponse } from 'shell/Api';
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';

// Re-export the shared API client, auth, and types
export { api, getApiBaseUrl, auth };
export type { ApiResponse };

/**
 * Get a URL for streaming an FTP file with authentication.
 * Used for audio preview in the podcast manager.
 *
 * Note: This returns a Promise because we need to get the current auth token.
 * The token is passed as a query parameter since <audio> elements can't set headers.
 *
 * @param ftpPath - The FTP file path to stream
 * @param companyId - Optional company ID for cross-company access (super_admin/multi-company users)
 * @returns Promise<string> - The authenticated stream URL
 */
export async function getFtpStreamUrl(ftpPath: string, companyId?: string): Promise<string> {
  const baseUrl = getApiBaseUrl();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User not authenticated');
  }

  // Get a fresh token
  const token = await user.getIdToken();

  // Build the stream URL with auth token as query param
  const url = new URL(`${baseUrl}/ftp/stream`);
  url.searchParams.set('path', ftpPath);
  url.searchParams.set('token', token);

  // Include company ID for cross-company access
  if (companyId) {
    url.searchParams.set('companyId', companyId);
  }

  return url.toString();
}

export default api;
